import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import React, {useEffect, useState} from 'react';

import { NumericTextBox } from '@progress/kendo-react-inputs';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import styles from './_ams-loan-cpi-dialog.module.scss';
import { Window } from '@progress/kendo-react-dialogs';
import { WindowActionsBar } from '@progress/kendo-react-dialogs';
import {ICpiDetails, IPostAmsLoan} from "../../../../Interfaces/Common/IPostAmsLoan";
import {Error} from "@progress/kendo-react-labels";
import { useConfiguration } from '../../../../Data/ConfigurationData';
import {PaymentFrequency, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";

interface CPIConfigurationProps {
  visible?: boolean;
  onClose: () => void;
  postLoanDetails: IPostAmsLoan
  updateCpiDetails: (loanCpiDetails: ICpiDetails | null, deferredDownCpiDetails: ICpiDetails | null, autoAccrueOneCpiPaymentWhenPosting: boolean) => void;
  defaultFactorAmount: number;
  showOptionForDeferredDown: boolean;
  paymentFrequency: PaymentFrequency;
}

export const AmsLoanCpiDialog = (props: CPIConfigurationProps) => {
  const [loanCpiDetails, setLoanCpiDetails] = useState<ICpiDetails | null>(props.postLoanDetails.loanCpiDetails);
  const [deferredDownCpiDetails, setDeferredDownCpiDetails] = useState<ICpiDetails | null>(props.postLoanDetails.deferredDownCpiDetails);
  const [autoAccrueOneCpiPaymentWhenPosting, setAutoAccrueOneCpiPaymentWhenPosting] = useState<boolean>(
    props.postLoanDetails.autoAccrueOneCpiPaymentWhenPosting ?? false
  );
  const [showDeferredDownColumn, setShowDeferredDownColumn] = useState<boolean>(!!props.postLoanDetails.deferredDownCpiDetails);
  const isSemiMonthly = props.paymentFrequency === PaymentFrequency.Semimonthly;
  const { data: configuration } = useConfiguration();


  useEffect(() => {
    const configDefault = configuration?.featureFlags?.[FeatureFlagIds.CPIAutoAccrueCheckedByDefault];
    if (typeof configDefault === 'boolean') {
      setAutoAccrueOneCpiPaymentWhenPosting(prev => {
        // If the user hasn't interacted yet (still equal to initial prop) then adopt the config default.
        if (prev === props.postLoanDetails.autoAccrueOneCpiPaymentWhenPosting) {
          return configDefault;
        }
        return prev;
      });
    }
  }, [configuration, props.postLoanDetails.autoAccrueOneCpiPaymentWhenPosting]);

  const cpiSemiDaysAreOutOfOrder = (cpiDetails: ICpiDetails | null): boolean => {
    if (!cpiDetails || !isSemiMonthly) {
      return false;
    }
    return cpiDetails.semiMonthlyPaymentDayOfMonth1 > cpiDetails.semiMonthlyPaymentDayOfMonth2;
  };
  const loanCpiSemiDaysAreOutOfOrder = cpiSemiDaysAreOutOfOrder(loanCpiDetails);
  const deferredDownCpiSemiDaysAreOutOfOrder = cpiSemiDaysAreOutOfOrder(deferredDownCpiDetails);
  const cpiSemiDaysAreInvalid = (cpiDetails: ICpiDetails | null): boolean => {
    if (!cpiDetails || !isSemiMonthly) {
      return false;
    }
    return cpiDetails.semiMonthlyPaymentDayOfMonth1 === 0 || cpiDetails.semiMonthlyPaymentDayOfMonth2 === 0;
  };
  const loanCpiSemiDaysAreInvalid = cpiSemiDaysAreInvalid(loanCpiDetails);
  const deferredDownCpiSemiDaysAreInvalid = cpiSemiDaysAreInvalid(deferredDownCpiDetails);
  const preventSave = loanCpiSemiDaysAreOutOfOrder
    || deferredDownCpiSemiDaysAreOutOfOrder
    || loanCpiSemiDaysAreInvalid
    || deferredDownCpiSemiDaysAreInvalid
  ;
  const handleSave = () => {
    props.updateCpiDetails(loanCpiDetails, showDeferredDownColumn ? deferredDownCpiDetails : null, autoAccrueOneCpiPaymentWhenPosting);
    props.onClose();
  };
  const onClose = () => {
    props.updateCpiDetails(null, null, false);
    props.onClose();
  };

  return (
    <Window
      title="CPI"
      modal={true}
      closeButton={() => <span></span>}
      minimizeButton={() => <span></span>}
      maximizeButton={() => <span></span>}
      className={styles.cpiConfigurationDialog}
      initialWidth={525}
      initialHeight={475}
      resizable={false}
    >
      <div className={styles.cpiConfigurationContent}>
        <div className={styles.cpiDetailsColumns}>
          <AmsLoanCpiDialogColumn
            title={"Loan CPI"}
            cpiDetails={loanCpiDetails}
            setCpiDetails={setLoanCpiDetails}
            defaultFactorAmount={props.defaultFactorAmount}
            isSemiMonthly={isSemiMonthly}
            showSemiDaysOutOfOrderErrorMessage={loanCpiSemiDaysAreOutOfOrder}
            showInvalidDaysErrorMessage={loanCpiSemiDaysAreInvalid}
          />
          {showDeferredDownColumn &&
            <AmsLoanCpiDialogColumn
              title={"Deferred Down CPI"}
              cpiDetails={deferredDownCpiDetails}
              setCpiDetails={setDeferredDownCpiDetails}
              defaultFactorAmount={props.defaultFactorAmount}
              isSemiMonthly={isSemiMonthly}
              showSemiDaysOutOfOrderErrorMessage={deferredDownCpiSemiDaysAreOutOfOrder}
              showInvalidDaysErrorMessage={deferredDownCpiSemiDaysAreInvalid}
            />
          }
        </div>

        <div className={styles.checkboxGroup}>
          {props.showOptionForDeferredDown &&
            <Checkbox
              value={showDeferredDownColumn}
              onChange={(e) => setShowDeferredDownColumn(e.value as boolean)}
              label="Apply CPI to Deferred Down"
            />
          }
          <Checkbox
            value={autoAccrueOneCpiPaymentWhenPosting}
            onChange={(e) => setAutoAccrueOneCpiPaymentWhenPosting(e.value as boolean)}
            label="Auto Accrue 1 CPI when posting deal, loan will show a CP record and the CPI balance will not be $0"
          />
        </div>
      </div>
      <WindowActionsBar>
        <Button
          onClick={handleSave}
          themeColor="primary"
          disabled={preventSave}
        >
          Save
        </Button>
        <Button
          onClick={onClose}
          themeColor="secondary"
        >
          Remove
        </Button>
      </WindowActionsBar>
    </Window>
  );
};

interface AmsLoanCpiDialogColumnProps {
  title: string;
  cpiDetails: ICpiDetails | null;
  defaultFactorAmount: number;
  setCpiDetails: (cpiDetails: ICpiDetails) => void;
  isSemiMonthly: boolean;
  showSemiDaysOutOfOrderErrorMessage: boolean;
  showInvalidDaysErrorMessage: boolean;
}

const AmsLoanCpiDialogColumn = (props: AmsLoanCpiDialogColumnProps) => {
  const cpiDetails = props.cpiDetails;

  const minDay = 0;
  const maxDay = 31;

  const handleUpdateCpiDetails = (fieldName: keyof ICpiDetails, value: any) => {
    if (!cpiDetails) {
      return;
    }

    props.setCpiDetails({
      ...cpiDetails,
      [fieldName]: value
    });
  };

  const createDefaultCpiDetails = (): void => {
    props.setCpiDetails({
      nextAccrueDate: new Date(),
      startDate: new Date(),
      factor: props.defaultFactorAmount,
      semiMonthlyPaymentDayOfMonth1: 0,
      semiMonthlyPaymentDayOfMonth2: 0
    });
  };

  useEffect(() => {
    if (!props.cpiDetails) {
      createDefaultCpiDetails();
    }
  }, []);
  return (
    <>
    {cpiDetails &&
      <div className={styles.cpiDetailsColumn}>
        <h3 className={styles.columnHeader}>{props.title}</h3>
        <div className={styles.formGroup}>
          <DatePickerWithYearSanitizer
            label={"Next Accrue Date"}
            value={cpiDetails.nextAccrueDate}
            onChange={(e) => handleUpdateCpiDetails("nextAccrueDate", e.value as Date)}
          />
        </div>
        <div className={styles.formGroup}>
          <DatePickerWithYearSanitizer
            label={"CPI Start"}
            value={cpiDetails.startDate}
            onChange={(e) => handleUpdateCpiDetails("startDate", e.value as Date)}
          />
        </div>
        <div className={styles.formGroup}>
          <NumericTextBox
            label={"CPI Factor"}
            value={cpiDetails.factor}
            onChange={(e) => handleUpdateCpiDetails("factor", e.value as number)}
            min={0}
            format="c2"
          />
        </div>
        {props.isSemiMonthly &&
          <>
            <div className={`${styles.formGroup} ${styles.semiGroup}`}>
              <div className={styles.semiInputs}>
                <NumericTextBox
                  label={"Semi 1"}
                  value={cpiDetails.semiMonthlyPaymentDayOfMonth1}
                  onChange={(e) => handleUpdateCpiDetails("semiMonthlyPaymentDayOfMonth1", e.value as number)}
                  format="n0"
                  width="100px"
                  min={minDay}
                  max={maxDay}
                />
                <NumericTextBox
                  label={"Semi 2"}
                  value={cpiDetails.semiMonthlyPaymentDayOfMonth2}
                  onChange={(e) => handleUpdateCpiDetails("semiMonthlyPaymentDayOfMonth2", e.value as number)}
                  format="n0"
                  width="100px"
                  min={minDay}
                  max={maxDay}
                />
              </div>
            </div>
            {props.showInvalidDaysErrorMessage && <Error>One or more days is set to 0</Error>}
            {props.showSemiDaysOutOfOrderErrorMessage && <Error>Day 1 cannot be after day 2</Error>}
          </>
        }
      </div>
      }
    </>
  );
}
